import { setCssVar } from "@/utils/setCssVar";
import { useEffect } from "react";

// 定义高度取值类型：最大值/最小值
type HeightTakeType = "max" | "min";

// 核心：基于三大高度属性计算可视区域高度
const calculateHeight = (element: HTMLElement, takeType: HeightTakeType = "min"): number => {
  if (!element) return 0;

  const { offsetHeight } = element;

  // 获取元素在视口的位置信息
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  // 计算元素在视口中的可见高度
  let visibleHeight = 0;
  // 元素顶部在视口内 或 底部在视口内（有交集）
  if (rect.bottom > 0 && rect.top < viewportHeight) {
    // 可见区域的顶部：取元素顶部和视口顶部的最大值
    const visibleTop = Math.max(rect.top, 0);
    // 可见区域的底部：取元素底部和视口底部的最小值
    const visibleBottom = Math.min(rect.bottom, viewportHeight);
    // 最终可见高度 = 可见底部 - 可见顶部
    visibleHeight = visibleBottom - visibleTop;
  }

  // 边界处理：根据传入的类型决定取最大/最小值
  return takeType === "max"
    ? Math.max(visibleHeight, offsetHeight) // 取可视高度和元素自身高度的最大值
    : Math.min(visibleHeight, offsetHeight); // 取可视高度和元素自身高度的最小值
};

// 通用Hook：监听元素可视区域高度并设置CSS变量
export function useElementHeightToCssVar({
  targetRef,
  cssVarKey = "",
  offset = 0,
  heightTakeType = "min",
  enabled = true, // 新增：是否启用此功能，默认启用
}: any) {
  useEffect(() => {
    // 如果禁用，则不执行任何操作
    if (!enabled) {
      return;
    }

    const targetElement = targetRef.current;
    if (!targetElement) return;

    // 更新CSS变量
    const updateCssVariable = () => {
      const visibleHeight = calculateHeight(targetElement, heightTakeType);
      setCssVar({ key: cssVarKey, value: `${visibleHeight - offset}px` });
    };

    // 初始化更新
    const timer = setTimeout(() => {
      clearTimeout(timer);
      updateCssVariable();
    }, 200);

    // 监听会导致三大高度属性变化的场景
    const observer = new MutationObserver(() => updateCssVariable());
    observer.observe(targetElement, {
      childList: true, // 子元素增删（影响scrollHeight）
      subtree: true, // 后代元素变化（影响scrollHeight）
      attributes: true, // 属性变化（比如style/class，影响offsetHeight/clientHeight）
      attributeFilter: ["style", "class", "height", "padding", "border"], // 精准监听高度相关属性
    });

    // 监听窗口变化（视口变化会影响可视高度计算）
    const handleResize = () => updateCssVariable();
    const handleScroll = () => updateCssVariable();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, { passive: true });

    // 清理
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
      setCssVar({ key: cssVarKey, value: "0px" });
    };
  }, [targetRef, cssVarKey, offset, heightTakeType, enabled]); // 添加enabled到依赖数组
}
